import { useCallback } from "react";

import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import { CRM_STATUS_CONFIG } from "app/pages/sales-intelligence/modules/manage-integrations/constants/crm";
import type { IntegrationStatusResponse } from "app/pages/sales-intelligence/sub-modules/management/components/types";
import type { Integration, IntegrationSyncStatus } from "app/pages/sales-intelligence/sub-modules/management/types";

import { manageIntegrationsQueryKeys } from "../key-factory/factory";

type CRMIntegrationStatusResult = Pick<
    UseQueryResult<IntegrationSyncStatus>,
    "isLoading" | "isFetching" | "refetch"
> & {
    data: IntegrationStatusResponse;
    silentRefetch: () => Promise<IntegrationSyncStatus>;
};

export const useCRMIntegrationStatusQuery = (integration: Integration): CRMIntegrationStatusResult => {
    const dispatch = useDispatch();
    const cfg = CRM_STATUS_CONFIG[integration];

    const data = useSelector(cfg.selectStatusResponse);

    const { isLoading, isFetching, refetch } = useQuery({
        queryKey: manageIntegrationsQueryKeys.byIntegration(integration),
        queryFn: () => dispatch(cfg.getStatusThunk(false)),
        staleTime: Infinity,
        refetchOnMount: true,
    });

    const silentRefetch = useCallback(() => dispatch(cfg.getStatusThunk(false)), [dispatch, cfg]);

    return {
        data,
        isLoading,
        isFetching,
        refetch,
        silentRefetch,
    };
};
